import { endOfDay, format, set, startOfDay, subDays } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dropdown, Icon, Loader, Message } from "semantic-ui-react";

import { DATE_FORMATS_FNS, DatePicker, DetailsCard, NavPortal, Tabs } from "components";
import { useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { LocationReportsTable, PercentStackedBarChart, SimpleBarChart, SimplePieChart, StackedBarChart, TotalPieChart } from "modules/LocationReports/components";
import { useLocationReports } from "modules/LocationReports/hooks/useLocationReports";
import "modules/LocationReports/LocationReports.scss";
import { toISOString } from "utils/dateHelperFunctions";

import type { UseQueryResult } from "@tanstack/react-query";
import type { ReportData } from "models";

enum REPORT_PERIOD {
  Yesterday = 1,
  Week = 7,
  Month = 30,
  Quarter = 90,
  Custom = "custom"
}

export type ReportThreshold = {
  name: string;
  color: string;
  y: number;
};

export type ParsedLocationReportData = Omit<ReportData, "report_data"> & {
  report_data: Record<string, string>[] | null;
};

type PERIOD = REPORT_PERIOD.Yesterday | REPORT_PERIOD.Week | REPORT_PERIOD.Month | REPORT_PERIOD.Quarter | REPORT_PERIOD.Custom;

const subtractDays = (days: number): Date => {
  const currentDate = new Date();
  const dateAfterSubtractingDays = subDays(currentDate, days);
  const dateWithTime = set(dateAfterSubtractingDays, { hours: 12, minutes: 0, seconds: 0 });
  return new Date(toISOString(dateWithTime));
};

export const LocationReports = () => {
  const { t } = useTranslation();

  const [showCustomRange, setShowCustomRange] = useState(false);
  const [dateFrom, setDateFrom] = useState<Date | null>(subtractDays(7));
  const [dateTo, setDateTo] = useState<Date | null>(subtractDays(1));
  const [expanded, setExpanded] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<PERIOD>(REPORT_PERIOD.Week);

  const { selectedLocation } = useSelectedLocation();

  const { groups, selectedGroup, setSelectedGroup, reports, loading } = useLocationReports({
    dealer_location_id: Number(selectedLocation?.id),
    date_from: dateFrom ? format(dateFrom, DATE_FORMATS_FNS.ISOFormatStartOfDayWithZ) : undefined,
    date_to: dateTo ? format(dateTo, DATE_FORMATS_FNS.ISOFormatEndOfDayWithZ) : undefined
  });

  const tabsData = new Map(
    reports.map(report => [
      `report_${report.data?.id}`,
      { id: `report_${report.data?.id}`, title: (report.data && t(`v8_${report.data.report_name}`)) || report.data?.report_name, data: [] }
    ])
  );

  const onSelectPeriod = (value: PERIOD) => {
    setSelectedPeriod(value);

    const period = (value === REPORT_PERIOD.Custom ? value : Number(value)) as PERIOD;

    switch (period) {
      case REPORT_PERIOD.Week:
      case REPORT_PERIOD.Month:
      case REPORT_PERIOD.Quarter:
        setDateFrom(subtractDays(period));
        setDateTo(subtractDays(1));
        setShowCustomRange(false);
        break;
      case REPORT_PERIOD.Yesterday:
        setDateFrom(subtractDays(1));
        setDateTo(subtractDays(1));
        setShowCustomRange(false);
        break;
      case REPORT_PERIOD.Custom:
        setShowCustomRange(true);
        break;
      default:
        break;
    }
  };

  const toggleExpand = () => {
    setExpanded(prev => !prev);
  };

  const handleDateChange = (...dates: (Date | null)[]) => {
    const [start, end] = dates;
    setDateFrom(start ? startOfDay(start) : null);
    setDateTo(end ? endOfDay(end) : null);
  };

  const periodLabels: Record<PERIOD, string> = {
    [REPORT_PERIOD.Yesterday]: `${t("v8_yesterday", "Yesterday")}`,
    [REPORT_PERIOD.Week]: `7 ${t("v8_days", "Days")}`,
    [REPORT_PERIOD.Month]: `30 ${t("v8_days", "Days")}`,
    [REPORT_PERIOD.Quarter]: `90 ${t("v8_days", "Days")}`,
    [REPORT_PERIOD.Custom]: t("v8_custom", "Custom")
  };

  const renderReports = (report: UseQueryResult<ParsedLocationReportData>, idx: number) => {
    if (report.isLoading) return <Loader active inline key={`reports-loader-${idx}`} />;

    if (report.isError) return <Message>{t("v8_failed_to_get_report", "Failed to get the report")}</Message>;

    if (!report.data) return null;

    if (!report.data.report_data?.length)
      return (
        <DetailsCard
          title={tabsData.get(`report_${report.data.id}`)?.title}
          sectionMenuId={`report_${report.data.id}`}
          key={`report_${report.data.id}`}
          isExpanded={expanded}
        >
          <div className="LocationReports__container-no-data">{t("v8_no_data", "No data")}</div>
        </DetailsCard>
      );

    const thresholds = report.data.thresholds || [];

    if (Array.isArray(thresholds) && thresholds.length > 0) {
      thresholds.forEach((th: ReportThreshold) => {
        if (th.name) th.name = t(th.name) || th.name;
      });
    }

    if (report.data.report_type === "SimplePieChart")
      return (
        <DetailsCard
          key={`report_${report.data.id}`}
          title={tabsData.get(`report_${report.data.id}`)?.title}
          sectionMenuId={`report_${report.data.id}`}
          isExpanded={expanded}
        >
          <SimplePieChart data={report.data.report_data} colors={report.data.report_colors || []} />
        </DetailsCard>
      );
    if (report.data.report_type === "TotalPieChart")
      return (
        <DetailsCard
          key={`report_${report.data.id}`}
          title={tabsData.get(`report_${report.data.id}`)?.title}
          sectionMenuId={`report_${report.data.id}`}
          isExpanded={expanded}
        >
          <TotalPieChart data={report.data.report_data} colors={report.data.report_colors || []} />
        </DetailsCard>
      );
    if (report.data.report_type === "SimpleBarChart")
      return (
        <DetailsCard
          key={`report_${report.data.id}`}
          title={tabsData.get(`report_${report.data.id}`)?.title}
          sectionMenuId={`report_${report.data.id}`}
          isExpanded={expanded}
        >
          <SimpleBarChart
            key={report.data.report_name}
            colors={report.data.report_colors || []}
            thresholds={Array.isArray(thresholds) ? thresholds : []}
            data={report.data.report_data}
            unit={report.data.unit}
          />
        </DetailsCard>
      );
    if (report.data.report_type === "StackedBarChart")
      return (
        <DetailsCard
          key={`report_${report.data.id}`}
          title={tabsData.get(`report_${report.data.id}`)?.title}
          sectionMenuId={`report_${report.data.id}`}
          isExpanded={expanded}
        >
          <StackedBarChart key={report.data.report_name} colors={report.data.report_colors || []} data={report.data.report_data} unit={report.data.unit} />
        </DetailsCard>
      );
    if (report.data.report_type === "PercentStackedBarChart")
      return (
        <DetailsCard
          key={`report_${report.data.id}`}
          title={tabsData.get(`report_${report.data.id}`)?.title}
          sectionMenuId={`report_${report.data.id}`}
          isExpanded={expanded}
        >
          <PercentStackedBarChart key={report.data.report_name} colors={report.data.report_colors || []} data={report.data.report_data} unit={report.data.unit} />
        </DetailsCard>
      );
    if (report.data.report_type === "Tabular")
      return (
        <DetailsCard
          key={`report_${report.data.id}`}
          title={tabsData.get(`report_${report.data.id}`)?.title}
          sectionMenuId={`report_${report.data.id}`}
          isExpanded={expanded}
        >
          <LocationReportsTable data={report.data.report_data} />
        </DetailsCard>
      );
  };

  return (
    <>
      <NavPortal>
        <div className="nav-bar-start LocationReports__dropdowns">
          <div className="LocationReports__dropdowns-container">
            <label htmlFor="location_report_group">{t("v8_report", "Report")}</label>
            <Dropdown
              id="location_report_group"
              className="nav-bar-map"
              button
              floating
              labeled
              selection
              value={Number(selectedGroup)}
              onChange={(_e, { value }) => setSelectedGroup(Number(value))}
              options={groups.data?.map(i => ({ key: i, value: Number(i), text: t(`v8_location_report_group_${i}`) || i })) || []}
            />

            <label htmlFor="location_report_period">{t("v8_select_time_period", "Select time period")}</label>
            <Dropdown
              id="location_report_period"
              className="nav-bar-map"
              button
              floating
              labeled
              selection
              value={selectedPeriod}
              onChange={(_e, { value }) => onSelectPeriod(value as PERIOD)}
              options={Object.values(REPORT_PERIOD)
                .filter(value => !!periodLabels[value as PERIOD])
                .map(value => ({
                  key: value,
                  text: periodLabels[value as PERIOD],
                  value
                }))}
            />
            {showCustomRange && (
              <>
                {t("v8_select_date_range", "Select date range")}
                <DatePicker onDateChange={handleDateChange} selectsRange startDate={dateFrom} endDate={dateTo} maxRange={90} showLabel />
              </>
            )}
          </div>
        </div>
      </NavPortal>
      <div className="LocationReports__container">
        <div className="LocationReports__container-header">
          <div className="LocationReports__container-header-wrapper">
            <div className="row">
              <Tabs data={tabsData} hideBorder offset={-250} />
              <div className="sub-row">
                <Button className="-appointment-status icon" size="small" onClick={toggleExpand}>
                  <Icon className={`arrows ${expanded ? "minimize" : "maximize"}`} color="green" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="LocationReports__container-content">
          {loading && <Loader active />}
          {reports.map((r, idx) => renderReports(r, idx))}
        </div>
      </div>
    </>
  );
};
